// Step 4 (alternate): Denial - Client denied assistance, collect reason

import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useVisitWizard } from '../../../../contexts/VisitWizardContext'
import { useUpdateVisit, useCreateVisitTransaction } from '../../../../hooks/useVisits'
import { Button } from '../../../../components/ui/button'
import { Checkbox } from '../../../../components/ui/checkbox'
import { Textarea } from '../../../../components/ui/textarea'
import { Label } from '../../../../components/ui/label'
import { Loader2 } from 'lucide-react'
import { toast } from 'sonner'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../../../../components/ui/alert-dialog'

// Denial reason options based on legacy form
const DENIAL_OPTIONS = [
  {
    id: 'option_1',
    label: 'You failed as an indigent, unless there is a court/government authorities\' order for PAO to act as counsel de oficio.',
  },
  {
    id: 'option_2',
    label: 'Acceptance of case on provisional basis only.',
  },
  {
    id: 'option_3',
    label: 'Public Attorneys do not act as prosecutors in criminal cases before court proceedings.',
  },
  {
    id: 'option_4',
    label: 'You have engaged the services of another counsel and there is no withdrawal of appearance thereof.',
  },
  {
    id: 'option_5',
    label: 'Advised to seek the services of private lawyer of own choice for not qualifying for PAO free legal services.',
  },
  {
    id: 'option_6',
    label: 'Others',
  },
]

export function Denial() {
  const navigate = useNavigate()
  const { goBack, setDenialReason, goToStep, state } = useVisitWizard()
  const updateVisit = useUpdateVisit()
  const createTransaction = useCreateVisitTransaction()
  const [selectedOptions, setSelectedOptions] = useState<Record<string, boolean>>({})
  const [textareaValues, setTextareaValues] = useState<Record<string, string>>({})
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [showConfirmDialog, setShowConfirmDialog] = useState(false)

  const handleCheckboxChange = (optionId: string, checked: boolean) => {
    setSelectedOptions(prev => ({ ...prev, [optionId]: checked }))
    if (!checked) {
      // Clear textarea value when unchecked
      setTextareaValues(prev => {
        const updated = { ...prev }
        delete updated[optionId]
        return updated
      })
    }
  }

  const handleTextareaChange = (optionId: string, value: string) => {
    setTextareaValues(prev => ({ ...prev, [optionId]: value }))
  }

  const handleSave = () => {
    // Build reasons array from selected options
    const reasons = Object.keys(selectedOptions)
      .filter(optionId => selectedOptions[optionId])
      .map(optionId => ({
        option: DENIAL_OPTIONS.find(opt => opt.id === optionId)?.label || '',
        reason: textareaValues[optionId] || '',
      }))

    if (reasons.length === 0) {
      toast.error('Please select at least one denial reason')
      return
    }

    // Show confirmation dialog
    setShowConfirmDialog(true)
  }

  const handleConfirm = async () => {
    if (!state.visitId) {
      toast.error('No visit ID found. Cannot save denial.')
      setShowConfirmDialog(false)
      return
    }

    setIsSubmitting(true)
    setShowConfirmDialog(false)

    try {
      // Build reasons array
      const reasons = Object.keys(selectedOptions)
        .filter(optionId => selectedOptions[optionId])
        .map(optionId => ({
          option: DENIAL_OPTIONS.find(opt => opt.id === optionId)?.label || '',
          reason: textareaValues[optionId] || '',
        }))

      // Save denial reasons to context
      const denialData = {
        reasons,
        timestamp: new Date().toISOString(),
      }
      setDenialReason(denialData)

      // Save denial transaction to backend (matches legacy behavior)
      // Type ID 101 = 'DENIED' from types table
      await createTransaction.mutateAsync({
        visit_id: state.visitId,
        transaction_type_id: 101, // DENIED type from types table
        transaction_detail: { reasons },
      })

      // Note: Visit status is already set to 101 (DENIED) during classification step
      // No need to update it again here

      toast.success('Visit denied successfully')

      // Navigate to summary page (matches legacy workflow)
      goToStep('summary')
    } catch (error: any) {
      console.error('Failed to save denial:', error)
      toast.error(error.message || 'Failed to save denial. Please try again.')
      setIsSubmitting(false)
    }
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="text-center">
        <h2 className="text-3xl font-bold text-primary">
          DENIAL/DISQUALIFICATION OF PAO LEGAL ASSISTANCE
        </h2>
        <p className="text-sm text-muted-foreground mt-2">
          Pursuant to Sec. 8 (c) of RA No. 9485 "Anti-Red Tape Act of 2007"
        </p>
      </div>

      {/* Denial Reasons */}
      <div className="space-y-6 px-4">
        {DENIAL_OPTIONS.map((option) => (
          <div key={option.id} className="flex items-start space-x-3">
            <Checkbox
              id={option.id}
              checked={selectedOptions[option.id] || false}
              onCheckedChange={(checked) =>
                handleCheckboxChange(option.id, checked as boolean)
              }
              className="mt-1"
            />
            <div className="flex-1 space-y-2">
              <Label
                htmlFor={option.id}
                className="text-sm font-normal cursor-pointer leading-relaxed"
              >
                {option.label}
              </Label>
              <Textarea
                value={textareaValues[option.id] || ''}
                onChange={(e) => handleTextareaChange(option.id, e.target.value)}
                disabled={!selectedOptions[option.id]}
                placeholder="Reason: Optional"
                rows={2}
                className="w-full disabled:opacity-50"
              />
            </div>
          </div>
        ))}
      </div>

      {/* Navigation Buttons */}
      <div className="flex justify-between pt-6">
        <Button
          type="button"
          variant="outline"
          onClick={goBack}
          disabled={isSubmitting}
        >
          Back
        </Button>
        <Button
          type="button"
          onClick={handleSave}
          disabled={Object.values(selectedOptions).filter(Boolean).length === 0 || isSubmitting}
          className="bg-accent hover:bg-accent/90"
        >
          {isSubmitting ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Saving...
            </>
          ) : (
            'Submit Denial'
          )}
        </Button>
      </div>

      {/* Confirmation Dialog - matches legacy "Do you wish to finish?" */}
      <AlertDialog open={showConfirmDialog} onOpenChange={setShowConfirmDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Warning</AlertDialogTitle>
            <AlertDialogDescription>
              Do you wish to finish? This will mark the visit as denied and proceed to the summary.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isSubmitting}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleConfirm}
              disabled={isSubmitting}
              className="bg-primary hover:bg-primary/90"
            >
              {isSubmitting ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Finishing...
                </>
              ) : (
                'Finish'
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
}
